import React, { useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { ArrowRight, Compass } from "lucide-react";
import api, { resolveAssetUrl } from "@/lib/api";
import useSEO from "@/hooks/useSEO";

const HERO_FALLBACK =
  "https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?auto=format&fit=crop&w=2000&q=85";

const Home = () => {
  useSEO({
    title: "Home",
    description: "Shaurya Travels — handcrafted tour packages and premium vehicle rentals from Pune & Mumbai. Ladakh, Kerala, Rajasthan, Goa, Bali, Dubai and more.",
    path: "/",
  });
  const { settings } = useOutletContext();
  const [photos, setPhotos] = useState([]);

  useEffect(() => {
    api.get("/photos").then((r) => setPhotos(r.data || [])).catch(() => setPhotos([]));
  }, []);

  const heroBg = settings?.hero_image_url ? resolveAssetUrl(settings.hero_image_url) : HERO_FALLBACK;
  const featured = photos.slice(0, 6);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative h-[100vh] w-full overflow-hidden">
        <img
          src={heroBg}
          alt="Hero"
          className="absolute inset-0 w-full h-full object-cover scale-105"
        />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 h-full flex flex-col justify-end pb-24">
          <div className="text-white max-w-3xl">
            <p className="text-xs uppercase tracking-[0.4em] opacity-80 mb-6 fade-up flex items-center gap-3">
              <Compass size={14} /> Est. wanderlust · India & beyond
            </p>
            <h1 className="font-serif-display text-5xl sm:text-7xl lg:text-8xl leading-[0.95] tracking-tight fade-up-2">
              {settings?.hero_title || "SHAURYA TRAVELS"}
            </h1>
            <p className="mt-8 text-base sm:text-lg max-w-xl opacity-90 fade-up-3 font-light">
              {settings?.hero_subtitle ||
                "Crafting unforgettable journeys across the world."}
            </p>
            <div className="mt-10 flex flex-wrap gap-4 fade-up-4">
              <Link
                to="/gallery"
                data-testid="hero-view-gallery-btn"
                className="btn-accent px-7 py-4 text-xs tracking-[0.25em] uppercase inline-flex items-center gap-3"
              >
                View Gallery <ArrowRight size={16} />
              </Link>
              <Link
                to="/contact"
                data-testid="hero-contact-btn"
                className="px-7 py-4 text-xs tracking-[0.25em] uppercase inline-flex items-center gap-3 border border-white/70 text-white hover:bg-white hover:text-[var(--st-ink)] transition-colors"
              >
                Plan a Journey
              </Link>
            </div>
          </div>
        </div>
        <div className="absolute bottom-8 right-8 z-10 text-white/70 text-xs tracking-[0.3em] uppercase rotate-90 origin-bottom-right hidden lg:block">
          Scroll
        </div>
      </section>

      {/* INTRO */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-24 lg:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-4">
          <p className="text-xs uppercase tracking-[0.4em] text-[var(--st-accent)] mb-6">
            — A note from us
          </p>
        </div>
        <div className="lg:col-span-8">
          <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-tight">
            We don't sell trips.
            <br />
            <span className="italic opacity-80">We frame memories.</span>
          </h2>
          <p className="mt-8 text-[var(--st-ink-2)] text-lg max-w-2xl leading-relaxed">
            For more than a decade, Shaurya Travels has handcrafted journeys for those who
            chase light, silence, and stories. Below, a few moments from the road —
            captured along the way.
          </p>
        </div>
      </section>

      {/* FEATURED GALLERY */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 pb-24">
        <div className="flex items-end justify-between mb-10">
          <h3 className="font-serif-display text-3xl sm:text-4xl">Recent Frames</h3>
          <Link
            to="/gallery"
            data-testid="home-view-all-link"
            className="text-xs uppercase tracking-[0.25em] nav-link"
          >
            View all →
          </Link>
        </div>
        {featured.length === 0 ? (
          <EmptyShowcase />
        ) : (
          <div className="columns-1 sm:columns-2 lg:columns-3 gap-6 space-y-6">
            {featured.map((p) => (
              <figure
                key={p.id}
                data-testid={`home-photo-${p.id}`}
                className="masonry-col overflow-hidden bg-[var(--st-surface)] group"
              >
                <img
                  src={resolveAssetUrl(p.url)}
                  alt={p.caption || "Travel photograph"}
                  className="w-full h-auto object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                />
                {p.caption && (
                  <figcaption className="px-1 py-3 text-xs uppercase tracking-[0.2em] text-[var(--st-ink-2)]">
                    {p.caption}
                  </figcaption>
                )}
              </figure>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

const EmptyShowcase = () => (
  <div
    data-testid="home-empty-gallery"
    className="border border-dashed border-[var(--st-border)] py-24 text-center"
  >
    <p className="font-serif-display text-2xl mb-3">No photographs yet</p>
    <p className="text-sm text-[var(--st-ink-2)]">
      Sign in to the admin panel to upload your first travel photographs.
    </p>
    <Link
      to="/admin/login"
      data-testid="home-empty-admin-link"
      className="inline-block mt-8 px-6 py-3 text-xs uppercase tracking-[0.25em] btn-accent"
    >
      Go to Admin
    </Link>
  </div>
);

export default Home;
